import axios from 'axios';
import { PortCollection } from 'types/Port';
import { objectKeysToCamelCase, objectKeysToSnakeCase } from 'utils/common';
import axiosWithToken from './axios-with-token';

export enum PerDiemQueryKeys {
  SmartPerDiemByTransaction = 'smartPerDiemByTransaction',
}

export interface EmptyReturnAvailableRow {
  id: string;
  datetime: string;
  isFightable: boolean | null;
  shift: string;
  reasonString: string | null;
  screenshotTimestamp: string | null;
}

export interface Filter {
  containerType: string;
  shippingLine: string;
  startDate: string;
  endDate: string;
  portCollection: string;
}

export async function getEmptyReturnAvailable(
  filter: Filter,
  signal?: AbortSignal
): Promise<EmptyReturnAvailableResponse> {
  const response = await axiosWithToken.get('core/api/v1/perdiem/empty-return-available', {
    signal,
    params: objectKeysToSnakeCase(filter),
  });
  return objectKeysToCamelCase(response.data);
}

export interface EmptyReturnAvailableResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: EmptyReturnAvailableRow[];
  dayCount: number | null;
  startDate: string | null;
  endDate: string | null;
  estimatedSavings: number | null;
  opportunityToFight: number | null;
  totalCost: number | null;
  portCollection: PortCollection | null;
}

export async function getEmptyReturnAvailableByTransaction(
  values: { transactionId: string; page?: number; pageSize?: number },
  signal?: AbortSignal
): Promise<EmptyReturnAvailableResponse> {
  const { transactionId, ...params } = values;
  try {
    const response = await axiosWithToken.get(
      `core/api/v1/perdiem/empty-return-available/${transactionId}`,
      {
        signal,
        params: objectKeysToSnakeCase(params),
      }
    );
    return objectKeysToCamelCase(response.data);
  } catch (e) {
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 403) {
        return {
          count: 0,
          next: null,
          previous: null,
          results: [],
          dayCount: null,
          startDate: null,
          endDate: null,
          estimatedSavings: null,
          opportunityToFight: null,
          totalCost: null,
          portCollection: null,
        };
      }
    }
    throw e;
  }
}

interface SmartScreenshotSuccessResponse {
  code: 'ok';
  url: string;
}

interface SmartScreenshotErrorResponse {
  code: 'no_credits' | 'no_screenshot' | 'error';
}

export type SmartScreenshotResponse = SmartScreenshotSuccessResponse | SmartScreenshotErrorResponse;

export async function getScreenshotUrl(values: {
  emptyReturnAvailabilityId: string;
}): Promise<SmartScreenshotResponse> {
  try {
    const response = await axiosWithToken.post<SmartScreenshotResponse>(
      'core/api/v1/perdiem/smart-screenshot',
      objectKeysToSnakeCase(values)
    );
    return response.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      if (e.response?.data?.code) {
        return e.response.data as SmartScreenshotErrorResponse;
      }
    }
    throw e;
  }
}

interface SmartReportResponse {
  code: 'ok' | 'invalid_payload' | 'no_fightable' | 'no_credits' | 'error';
}

export async function sendSmartReport(values: {
  filter: Filter;
  containers: string[];
  proof: 'all' | 'returns_only';
  emails: string[];
}): Promise<SmartReportResponse> {
  try {
    const response = await axiosWithToken.post<SmartReportResponse>(
      'core/api/v1/perdiem/smart-report',
      objectKeysToSnakeCase(values, true)
    );
    return response.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      if (['no_credits'].includes(e.response?.data?.code)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return e.response.data as SmartReportResponse;
      }
    }
    throw e;
  }
}
